import { useTranslation } from 'react-i18next';

import { VoiceInput } from '../../voice/components/VoiceInput';
import { TaskForm, type TaskFormValues } from '../components/TaskForm';
import { type UpdateTaskPatch } from '../api/tasksApi';
import { useTasks } from '../hooks/useTasks';
import { useUpdateTask } from '../hooks/useUpdateTask';
import { isSizeDowngradeConflict } from '../lib/updateTaskError';
import styles from './TaskFormScreen.module.css';

interface TaskEditScreenProps {
  taskId: number;
  onClose: () => void;
  // PARA-131 (строка 21 Change Table) — проп сквозной по дереву экранов задач.
  // TaskQuotaNotice здесь недостижим (PATCH /tasks/{id} квоту не проверяет),
  // но PARA-132 (AC-16) использует его для перехода в биллинг из голосового
  // ввода: gating по тарифу одинаков на обоих экранах формы.
  onOpenBilling: () => void;
}

// PARA-128 (FE-9) — экран редактирования: начальные значения берём из кэша
// useTasks() по taskId (отдельного GET /tasks/{id} нет и он вне scope).
// PATCH шлётся только с изменёнными полями (diff vs initial, AC-3).
export function TaskEditScreen({
  taskId,
  onClose,
  onOpenBilling,
}: TaskEditScreenProps): JSX.Element {
  const { t } = useTranslation('tasks');
  const { data: tasks = [], isLoading: isTasksLoading } = useTasks();
  const { mutate, isPending, isError, error } = useUpdateTask();

  const task = tasks.find((candidate) => candidate.id === taskId);

  // EC-7 — симметрично TaskDetailScreen: пока список задач грузится, показываем
  // нейтральное пустое состояние, а не notFound (isLoading, не isPending).
  if (isTasksLoading) {
    return <div className={styles.screen} />;
  }

  if (task === undefined) {
    return (
      <div className={styles.screen}>
        <h2 className={styles.heading}>{t('form.editTitle')}</h2>
        <p className={styles.empty}>{t('form.errors.notFound')}</p>
      </div>
    );
  }

  const initial: TaskFormValues = {
    title: task.title,
    size: task.size,
    priority: task.priority,
    estimatedDurationMinutes: task.estimated_duration_minutes,
  };

  const handleSubmit = (values: TaskFormValues): void => {
    const patch: UpdateTaskPatch = {};
    if (values.title !== initial.title) {
      patch.title = values.title;
    }
    if (values.size !== initial.size) {
      patch.size = values.size;
    }
    if (values.priority !== initial.priority) {
      patch.priority = values.priority;
    }
    // PARA-135 (AC-31): очистка поля — это тоже изменение. В патч уезжает
    // явный null, и сервер сбрасывает оценку (ТД-14). Правило «значение не
    // изменилось → в патч не попадает» сохраняется: оно закрывает AC-E17 со
    // стороны Mini App — лишнего запроса нет.
    if (values.estimatedDurationMinutes !== initial.estimatedDurationMinutes) {
      patch.estimated_duration_minutes = values.estimatedDurationMinutes;
    }

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    mutate({ taskId, patch }, { onSuccess: onClose });
  };

  // AC-E3a: 409 «понижение размера у задачи с подзадачами» получает отдельную
  // подпись — на общем submitFailed пользователь не понял бы причину отказа.
  const submitErrorMessage = isSizeDowngradeConflict(error)
    ? t('form.errors.sizeDowngradeBlocked')
    : t('form.errors.submitFailed');

  return (
    <div className={styles.screen}>
      <h2 className={styles.heading}>{t('form.editTitle')}</h2>
      <TaskForm
        initial={initial}
        submitLabel={t('form.submitEdit')}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        error={isError ? submitErrorMessage : null}
        renderVoiceInput={(api) => <VoiceInput {...api} onOpenBilling={onOpenBilling} />}
      />
    </div>
  );
}
